import React, { useState } from 'react';
import { useEffect } from 'react';
import { Search, Filter, Phone, Play, MoreHorizontal, PhoneCall, Globe, Linkedin, FileText, MessageSquare, X, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { useGoogleSheets } from '../../hooks/useGoogleSheets';
import StatsCard from '../Dashboard/StatsCard';

const CallAgentPage = () => {
  const { data: allCallLeads, isLoading, error, isAuthenticated } = useGoogleSheets('call-leads');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalContent, setModalContent] = useState('');
  const itemsPerPage = 10;

  const filteredLeads = allCallLeads.filter(lead => {
    const matchesSearch = (lead.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (lead.dm_full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (lead.lead_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (lead.dm_mobile_1 || '').includes(searchTerm);
    const matchesStatus = statusFilter === 'all' || lead.lead_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const callLeads = filteredLeads.slice(startIndex, startIndex + itemsPerPage);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const getLeadStatusTag = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'to call':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
            To Call
          </span>
        );
      case 'calling':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
            Calling
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
            Failed
          </span>
        );
      case 'booked':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
            Booked
          </span>
        );
      case 'follow up':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
            Follow Up
          </span>
        );
      case 'closed':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
            Closed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
            {status || 'Unknown'}
          </span>
        );
    }
  };

  const getOutcomeTag = (outcome: string) => {
    // Return null for empty, N/A, or whitespace-only values
    if (!outcome || outcome.trim() === '' || outcome.toLowerCase() === 'n/a') {
      return null;
    }
    
    switch (outcome?.toLowerCase()) {
      case 'connected':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
            Connected
          </span>
        );
      case 'not connected':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
            Not Connected
          </span>
        );
      case 'call later':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
            Call Later
          </span>
        );
      case 'info requested':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
            Info Requested
          </span>
        );
      case 'not interested':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
            Not Interested
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
            {outcome}
          </span>
        );
    }
  };

  const handleOpenModal = (title: string, content: string) => {
    setModalTitle(title);
    setModalContent(content);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalTitle('');
    setModalContent('');
  };

  // Only show loading if we're loading AND have no data to display
  if (isLoading && allCallLeads.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-gray-500 text-lg mb-2">Loading data...</div>
          <div className="text-gray-400 text-sm">Please Make Sure The System Is Connected</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Connection Status Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-lg flex items-center space-x-2 mb-6">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Total Calls"
          value={allCallLeads.reduce((sum, lead) => sum + (parseInt(lead.call_attempts) || 0), 0)}
          icon={Phone}
          color="blue"
        />
        <StatsCard
          title="Connected"
          value={allCallLeads.reduce((sum, lead) => sum + (parseInt(lead.pickups) || 0), 0)}
          icon={PhoneCall}
          color="green"
        />
        <StatsCard
          title="Follow Ups"
          value={allCallLeads.filter(lead => lead.lead_status === 'follow up').length}
          icon={Phone}
          color="orange"
        />
        <StatsCard
          title="Booked"
          value={allCallLeads.filter(lead => lead.lead_status === 'booked').length}
          icon={Phone}
          color="purple"
        />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter size={16} className="text-gray-500" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="to call">To Call</option>
                <option value="calling">Calling</option>
                <option value="failed">Failed</option>
                <option value="booked">Booked</option>
                <option value="follow up">Follow Up</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-fixed">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-center px-2 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  Lead ID
                </th>
                <th className="text-left px-2 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-36">
                  Person
                </th>
                <th className="text-left px-2 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                  Company
                </th>
                <th className="text-center px-2 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  Calls/Pickups
                </th>
                <th className="text-center px-2 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  Last Call Outcome
                </th>
                <th className="text-center px-2 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  Next Call
                </th>
                <th className="text-center px-2 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  Lead Status
                </th>
                <th className="text-center px-2 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  Convo Record
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {callLeads.map((lead) => (
                <tr key={lead.lead_id || lead._rowIndex} className="hover:bg-gray-50 transition-colors">
                  <td className="px-2 py-4 text-center">
                    <div className="text-sm font-medium text-gray-900 break-words">
                      {lead.lead_id || 'N/A'}
                    </div>
                  </td>
                  <td className="px-2 py-4">
                    <div className="flex items-center space-x-1 mb-1">
                      <span className="text-sm font-medium text-gray-900 break-words">
                        {lead.dm_full_name || 'N/A'}
                      </span>
                      {lead.dm_linkedin && (
                        <a 
                          href={lead.dm_linkedin.startsWith('http') ? lead.dm_linkedin : `https://${lead.dm_linkedin}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="LinkedIn Profile"
                        >
                          <Linkedin size={12} />
                        </a>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 break-words">{lead.dm_title || 'N/A'}</div>
                    {lead.dm_mobile_1 && (
                      <div className="text-xs text-gray-600 break-words">{lead.dm_mobile_1}</div>
                    )}
                    {lead.dm_mobile_2 && (
                      <div className="text-xs text-gray-600 break-words">{lead.dm_mobile_2}</div>
                    )}
                  </td>
                  <td className="px-2 py-4">
                    <div className="text-sm font-medium text-gray-900 break-words mb-2">
                      {lead.company_name || 'N/A'}
                    </div>
                    <div className="flex items-center space-x-2">
                      {lead.company_website && (
                        <a 
                          href={lead.company_website.startsWith('http') ? lead.company_website : `https://${lead.company_website}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="Visit Website"
                        >
                          <Globe size={14} />
                        </a>
                      )}
                      {lead.company_linkedin && (
                        <a 
                          href={lead.company_linkedin.startsWith('http') ? lead.company_linkedin : `https://${lead.company_linkedin}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="LinkedIn Profile"
                        >
                          <Linkedin size={14} />
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-4 text-center">
                    <div className="text-sm text-gray-900">
                      {lead.call_attempts || 0}/{lead.pickups || 0}
                    </div>
                  </td>
                  <td className="px-2 py-4 text-center">
                    {getOutcomeTag(lead.last_call_outcome)}
                  </td>
                  <td className="px-2 py-4 text-center">
                    <div className="text-sm text-gray-900">
                      {lead.next_call ? format(new Date(lead.next_call), 'MMM dd, yyyy HH:mm') : ''}
                    </div>
                  </td>
                  <td className="px-2 py-4 text-center">
                    {getLeadStatusTag(lead.lead_status)}
                  </td>
                  <td className="px-2 py-4 text-center">
                    <div className="flex items-center space-x-2">
                      {lead.last_call_transcript && (
                        <button 
                          onClick={() => handleOpenModal('Call Transcript', lead.last_call_transcript)}
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="View Transcript"
                        >
                          <FileText size={14} />
                        </button>
                      )}
                      {lead.last_call_recording && (
                        <button 
                          onClick={() => handleOpenModal('Call Recording', lead.last_call_recording)}
                          className="text-green-600 hover:text-green-800 transition-colors"
                          title="Play Recording"
                        >
                          <Play size={14} />
                        </button>
                      )}
                      {lead.conversation_history && (
                        <button 
                          onClick={() => handleOpenModal('Conversation History', lead.conversation_history)}
                          className="text-purple-600 hover:text-purple-800 transition-colors"
                          title="View History"
                        >
                          <MessageSquare size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="bg-white rounded-xl border border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredLeads.length)} of {filteredLeads.length} results
          </div>
          {totalPages > 1 && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-2 text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{modalTitle}</h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {modalTitle === 'Call Recording' ? (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">Recording URL:</p>
                  <a 
                    href={modalContent}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline break-all"
                  >
                    {modalContent}
                  </a>
                  <div className="mt-4">
                    <audio controls className="w-full">
                      <source src={modalContent} type="audio/mpeg" />
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                </div>
              ) : (
                <div className="whitespace-pre-wrap text-sm text-gray-700 break-words">
                  {modalContent || 'No content available'}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {filteredLeads.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg">No call leads found</div>
          <div className="text-gray-400 text-sm mt-1">
            {searchTerm || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Call leads will appear here once they\'re added to the system'}
          </div>
        </div>
      )}
    </div>
  );
};

export default CallAgentPage;